// src/utils/apiWithRefresh.js
// Production-ready API utility with auto-refresh, deduplication, and proper error handling
import Api from "./api";
import { getValidAccessToken, refreshAccessToken } from "./tokenService";

// Singleton refresh lock to prevent concurrent refresh requests
let isRefreshing = false;
let failedQueue = [];

async function processQueue(error, token = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

function handleAuthError(message = "Session expired. Please login again.") {
  if (typeof window !== "undefined") {
    if (window.showInvalidTokenMessage) {
      window.showInvalidTokenMessage(message);
    } else {
      console.error(message);
      // Fallback redirect for SSR safety
      window.location.href = "/login";
    }
  }
}

function parseErrorStatus(err) {
  // Robust status extraction
  const msg = (err.message || "").toLowerCase();
  if (err.status) return err.status;
  if (msg.includes("403") || msg.includes("forbidden")) return 403;
  if (
    msg.includes("401") ||
    msg.includes("unauthorized") ||
    msg.includes("invalid token") ||
    msg.includes("expired token") ||
    msg.includes("jwt")
  )
    return 401;
  return null;
}

async function apiRequestWithRefresh(endpoint, method = "GET", body = null) {
  let token = await getValidAccessToken();

  if (!token) {
    handleAuthError("No valid token available");
    throw new Error("No valid token - authentication required");
  }

  const request = () => Api.request(endpoint, method, body, token);

  try {
    return await request();
  } catch (err) {
    const status = parseErrorStatus(err);

    // 403: Unauthorized - immediate redirect
    if (status === 403) {
      if (typeof window !== "undefined") {
        window.location.href = "/unauthorized";
      }
      throw new Error("Access forbidden");
    }

    // 401: Try refresh (if not already refreshing)
    if (status === 401) {
      if (isRefreshing) {
        // Queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => request())
          .catch((err) => {
            handleAuthError();
            throw err;
          });
      }

      // First 401 - trigger refresh
      isRefreshing = true;

      try {
        const newToken = await refreshAccessToken();
        processQueue(null, newToken);
        return await request(); // Retry with new token
      } catch (refreshError) {
        processQueue(refreshError);
        handleAuthError();
        throw new Error("Token refresh failed - please login again");
      } finally {
        isRefreshing = false;
      }
    }

    // All other errors: re-throw
    throw err;
  }
}

const apiWithRefresh = {
  get: (endpoint) => apiRequestWithRefresh(endpoint, "GET"),
  post: (endpoint, body) => apiRequestWithRefresh(endpoint, "POST", body),
  put: (endpoint, body) => apiRequestWithRefresh(endpoint, "PUT", body),
  patch: (endpoint, body) => apiRequestWithRefresh(endpoint, "PATCH", body),
  delete: (endpoint) => apiRequestWithRefresh(endpoint, "DELETE"),
};

export default apiWithRefresh;
